/* Circle physics for the tech-stack ball pit: gravity, wall and ball-to-ball
   collisions, plus two outside forces — scroll speed and the pointer.
   Pure maths, no DOM, so it can be exercised outside the browser. */

const GRAVITY = 2400; // px/s²
const BOUNCE = 0.38;
const AIR_DRAG = 0.5;
const POINTER_REACH = 64; // px beyond a ball's edge at which the cursor pushes it
const SOLVER_PASSES = 8;
const REST_SPEED = 45; // px/s below which a ball is eased to a stop

export const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// `sizes` are ball diameters at full scale, in px
export function createPit(sizes) {
  const balls = sizes.map((size, i) => ({
    base: size / 2,
    seed: ((i * 9301 + 49297) % 233280) / 233280, // stable per-ball randomness
    r: size / 2,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    inside: false,
  }));
  let width = 0;
  let height = 0;

  // Scale the balls so they fill a steady share of the pit at any size
  function resize(w, h) {
    width = w;
    height = h;
    const area = balls.reduce((sum, b) => sum + Math.PI * b.base * b.base, 0);
    const scale = clamp(Math.sqrt((width * height * 0.42) / area), 0.5, 1.2);
    balls.forEach((b) => {
      b.r = b.base * scale;
      b.x = clamp(b.x, b.r, width - b.r);
    });
  }

  // Queue every ball above the open top, staggered so they rain in
  function drop() {
    balls.forEach((b, i) => {
      b.x = b.r + b.seed * (width - 2 * b.r);
      b.y = -b.r - i * 46;
      b.vx = (b.seed - 0.5) * 260;
      b.vy = 0;
      b.inside = false;
    });
  }

  function collide() {
    for (let i = 0; i < balls.length; i++) {
      const a = balls[i];
      for (let j = i + 1; j < balls.length; j++) {
        const b = balls[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const min = a.r + b.r;
        const distSq = dx * dx + dy * dy;
        if (distSq >= min * min || distSq === 0) continue;

        const dist = Math.sqrt(distSq);
        const nx = dx / dist;
        const ny = dy / dist;
        const ma = a.r * a.r;
        const mb = b.r * b.r;
        // Push the pair apart, the lighter ball moving further
        const overlap = min - dist;
        a.x -= nx * overlap * (mb / (ma + mb));
        a.y -= ny * overlap * (mb / (ma + mb));
        b.x += nx * overlap * (ma / (ma + mb));
        b.y += ny * overlap * (ma / (ma + mb));

        const closing = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
        if (closing >= 0) continue;
        // Resting contacts don't bounce, which keeps the pile from jittering
        const e = closing < -60 ? BOUNCE : 0;
        const impulse = (-(1 + e) * closing) / (1 / ma + 1 / mb);
        a.vx -= (impulse / ma) * nx;
        a.vy -= (impulse / ma) * ny;
        b.vx += (impulse / mb) * nx;
        b.vy += (impulse / mb) * ny;
      }
    }
  }

  function walls(b) {
    if (b.x < b.r) {
      b.x = b.r;
      b.vx = Math.abs(b.vx) * BOUNCE;
    } else if (b.x > width - b.r) {
      b.x = width - b.r;
      b.vx = -Math.abs(b.vx) * BOUNCE;
    }
    if (b.y > height - b.r) {
      b.y = height - b.r;
      b.vy = b.vy > 60 ? -b.vy * BOUNCE : 0;
    }
    // The top stays open until a ball has fallen in, then it becomes a ceiling
    if (b.y > b.r) b.inside = true;
    else if (b.inside) {
      b.y = b.r;
      b.vy = Math.abs(b.vy) * BOUNCE;
    }
  }

  /* Advance by dt seconds.
     scrollSpeed: page scroll velocity in px/s — it "shakes" the pit, cancelling
       gravity and sloshing the balls sideways.
     pointer: { x, y, vx, vy, active } in pit coordinates — balls are repelled
       from it and pick up some of its sweep velocity. */
  function step(dt, scrollSpeed, pointer) {
    const lift = Math.min(Math.abs(scrollSpeed) * 2.2, 6800);
    const slosh = scrollSpeed * 0.9;
    const drag = Math.exp(-AIR_DRAG * dt);

    balls.forEach((b) => {
      b.vy += (GRAVITY - lift) * dt;
      b.vx += (b.seed - 0.5) * slosh * dt;

      if (pointer && pointer.active) {
        const dx = b.x - pointer.x;
        const dy = b.y - pointer.y;
        const dist = Math.hypot(dx, dy);
        const reach = b.r + POINTER_REACH;
        if (dist < reach && dist > 0.01) {
          const near = 1 - dist / reach;
          b.vx += ((dx / dist) * 9000 + pointer.vx * 14) * near * dt;
          b.vy += ((dy / dist) * 9000 + pointer.vy * 14) * near * dt;
        }
      }

      b.vx *= drag;
      b.vy *= drag;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
    });

    for (let pass = 0; pass < SOLVER_PASSES; pass++) {
      collide();
      balls.forEach(walls);
    }

    // Bleed off the last trembles so a settled pile sits still
    balls.forEach((b) => {
      if (b.vx * b.vx + b.vy * b.vy < REST_SPEED * REST_SPEED) {
        b.vx *= 0.8;
        b.vy *= 0.8;
      }
    });
  }

  return { balls, resize, drop, step };
}
